import { makeUrl } from '@ms-cloudpack/path-string-parsing';
import { apiServerPort } from '@ms-cloudpack/api-server';

/**
 * Creates API server URLs for remote connection
 *
 * Generates WebSocket URLs for connecting to remote API servers based on
 * the provided remote host and available ports.
 *
 * If the remote host is not specified, it starts from 'localhost'.
 * It accepts both HTTP and HTTPS protocols, and generates the appropriate
 * WebSocket URLs based on the protocol of the remote host and known api server ports.
 * It also accepts WS and WSS protocols based on the remote host's protocol and returns as is.
 *
 * When the protocol is undefined (not HTTP or HTTPS), it generates both secure (wss://)
 * and non-secure (ws://) WebSocket URLs, with wss:// URLs listed first.
 */
export function createRemoteApiUrls(params: { remote: string | boolean; apiServerUrl: string }): string[] {
  const remote = typeof params.remote === 'string' ? params.remote : 'localhost';

  // Extract the protocol from the remote if available
  let remoteProtocol: string | undefined = undefined;
  try {
    remoteProtocol = makeUrl(remote).protocol;
  } catch {
    // Ignore error and proceed with undefined protocol
  }

  // Return as is if it is a WebSocket URL
  if (remoteProtocol === 'ws:' || remoteProtocol === 'wss:') {
    return [remote];
  }

  // Get the hostname (host without port, protocol, etc.). Use dummy protocol to extract the hostname if needed.
  const hostname = remote.startsWith('http') ? makeUrl(remote).hostname : makeUrl(`http://${remote}`).hostname;

  // Generate URLs based on protocol
  let hostApiServerURLs: string[] = [];

  if (remoteProtocol === 'https:') {
    // For HTTPS, only use secure WebSockets
    hostApiServerURLs = apiServerPort.map((port) => `wss://${hostname}:${port}`);
  } else if (remoteProtocol === 'http:') {
    // For HTTP, only use non-secure WebSockets
    hostApiServerURLs = apiServerPort.map((port) => `ws://${hostname}:${port}`);
  } else {
    // For undefined protocol, generate both secure and non-secure URLs
    // First add all wss:// URLs
    const wssUrls = apiServerPort.map((port) => `wss://${hostname}:${port}`);

    // Then add all ws:// URLs
    const wsUrls = apiServerPort.map((port) => `ws://${hostname}:${port}`);

    // Combine both sets of URLs with wss URLs first
    hostApiServerURLs = [...wssUrls, ...wsUrls];
  }

  // Exclude the current api url from the list
  return hostApiServerURLs.filter((url) => url !== params.apiServerUrl);
}
